import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'
import Highlighter from 'react-highlight-words'
import ReactResizeDetector from 'react-resize-detector'
import { buildSearchHighlightPatterns } from 'shared/utils/stringUtils'

export const SEARCH_MATCH_TEXT_COLOR = '#1A7FA0'
export const SEARCH_MATCH_HIGHLIGHT_BACKGROUND = '#FFF9C4'

const useStyles = makeStyles({
    contentDiv: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    highlightWrapper: {
        position: 'relative',
    },
    highlightedEllipsis: {
        position: 'absolute',
        top: '50%',
        right: 0,
        transform: 'translateY(-50%)',
        height: '1em',
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: SEARCH_MATCH_HIGHLIGHT_BACKGROUND,
        color: SEARCH_MATCH_TEXT_COLOR,
        fontWeight: 500,
        fontSize: pxToRem(12),
        lineHeight: 1,
        padding: 0,
        pointerEvents: 'none',
    },
    searchTitleText: {
        fontSize: pxToRem(12),
        lineHeight: 1.4,
    },
})

const hasSearchMatchInText = (text: string, searchText: string): boolean => {
    if (!searchText?.trim()) return false
    return buildSearchHighlightPatterns(searchText).some((pattern) => {
        try {
            return new RegExp(pattern, 'i').test(text)
        } catch {
            return false
        }
    })
}

interface SingleLineEllipsisForSearchProps {
    /**
     * Callback for when the truncation state changes.
     */
    onTruncate?: () => void

    /**
     * Text content.
     */
    text: string

    /**
     * Searched text in search bar
     */
    searchText?: string
    /**
     * Default width of highlighted text
     */
    defaultWidth?: string | number
}

/**
 *
 * This component truncates long texts, and shows
 * the full text in a tooltip.
 */
const SingleLineEllipsisForSearch = ({
    onTruncate,
    text = '',
    searchText = '',
    defaultWidth = 200,
}: SingleLineEllipsisForSearchProps) => {
    const classes = useStyles()
    const [divRef, setDivRef] = React.useState<HTMLDivElement | undefined>()
    const [isTruncated, setIsTruncated] = React.useState(false)
    const [isMatchClipped, setIsMatchClipped] = React.useState(false)

    const setRef = (element: HTMLDivElement) => {
        setDivRef(element)
    }

    const recomputeMatchClipped = (truncated: boolean) => {
        if (!divRef || !truncated || !hasSearchMatchInText(text, searchText)) {
            setIsMatchClipped(false)
            return
        }
        const containerRight = divRef.getBoundingClientRect().right
        const marks = divRef.querySelectorAll('mark')
        const clipped = Array.from(marks).some(
            (mark) => mark.getBoundingClientRect().right > containerRight
        )
        setIsMatchClipped(clipped)
    }

    const handleResize = (width: number) => {
        if (divRef) {
            const newTruncated = divRef.scrollWidth > Math.round(width)
            setIsTruncated(newTruncated)
            // Recompute inline so the marker also unhighlights on resizes that
            // don't cross the truncation threshold (the layout effect below
            // only fires when isTruncated/text/searchText change).
            recomputeMatchClipped(newTruncated)
            onTruncate?.()
        }
    }

    React.useLayoutEffect(() => {
        recomputeMatchClipped(isTruncated)
    }, [divRef, text, searchText, isTruncated])

    return (
        <div className={classes.contentDiv}>
            <HoverTooltip text={isTruncated ? text : ''}>
                <div className={classes.highlightWrapper}>
                    <div ref={setRef} className={classes.contentDiv}>
                        <span>
                            <Highlighter
                                className={classes.searchTitleText}
                                style={{
                                    width: defaultWidth,
                                }}
                                autoEscape={false}
                                searchWords={buildSearchHighlightPatterns(searchText)}
                                textToHighlight={text}
                                highlightStyle={{
                                    backgroundColor: SEARCH_MATCH_HIGHLIGHT_BACKGROUND,
                                    fontWeight: 500,
                                    color: SEARCH_MATCH_TEXT_COLOR,
                                    padding: 0,
                                }}
                            />
                        </span>
                    </div>
                    {isMatchClipped && (
                        <mark
                            className={classes.highlightedEllipsis}
                            aria-hidden="true"
                        >
                            …
                        </mark>
                    )}
                </div>
            </HoverTooltip>
            {divRef && (
                <ReactResizeDetector
                    handleWidth
                    onResize={handleResize}
                    targetDomEl={divRef}
                />
            )}
        </div>
    )
}

export default SingleLineEllipsisForSearch
